import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowSquareOut, CaretDown, PencilSimple, Trash, ShieldSlash } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const EDIT_FIELDS = [
  { key: "name",        label: "Nom",         type: "text",  required: true },
  { key: "phone",       label: "Téléphone",   type: "tel" },
  { key: "email",       label: "Email",       type: "email" },
  { key: "website",     label: "Site web",    type: "url" },
  { key: "address",     label: "Adresse",     type: "text" },
  { key: "city",        label: "Ville",       type: "text" },
  { key: "postal_code", label: "Code postal", type: "text" },
  { key: "category",    label: "Catégorie",   type: "text" },
];

export default function BusinessRowActions({ business, onUpdated, onDeleted }) {
  const [editing, setEditing] = useState(false);

  const confirmDelete = async () => {
    if (!window.confirm(`Supprimer définitivement "${business.name}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/businesses/${business.id}`);
      toast.success(`Fiche « ${business.name} » supprimée`);
      onDeleted?.(business.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  const removeDnc = async () => {
    if (!window.confirm(`Lever le blocage « Ne plus contacter » sur « ${business.name} » ?\n\nCette entreprise pourra à nouveau apparaître dans les listes d'appels et recevoir des emails d'outreach.`)) return;
    try {
      await api.post(`/admin/businesses/${business.id}/dnc/remove`);
      toast.success(`« ${business.name} » peut à nouveau être contactée.`);
      onUpdated?.(business.id, { dnc: false, dnc_at: null, dnc_by_telepro_code: null, dnc_reason: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="inline-flex items-center gap-1 border border-border hover:border-foreground hover:bg-accent px-2.5 py-1.5 rounded-sm text-xs font-semibold"
            data-testid={`biz-actions-${business.id}`}
          >
            Actions <CaretDown size={12} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {business.slug && (
            <DropdownMenuItem asChild data-testid={`biz-view-${business.id}`}>
              <Link to={`/entreprise/${business.slug}`} target="_blank">
                <ArrowSquareOut size={14} className="mr-2" /> Voir la fiche
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setEditing(true)} data-testid={`biz-edit-${business.id}`}>
            <PencilSimple size={14} className="mr-2" /> Éditer
          </DropdownMenuItem>
          {business.dnc && (
            <DropdownMenuItem onClick={removeDnc} data-testid={`biz-remove-dnc-${business.id}`}>
              <ShieldSlash size={14} className="mr-2" /> Lever le DNC
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={confirmDelete}
            className="text-destructive focus:text-destructive"
            data-testid={`biz-delete-${business.id}`}
          >
            <Trash size={14} className="mr-2" /> Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {editing && (
        <EditBusinessModal
          business={business}
          onClose={() => setEditing(false)}
          onSaved={(patch) => {
            onUpdated?.(business.id, patch);
            setEditing(false);
          }}
        />
      )}
    </>
  );
}

function EditBusinessModal({ business, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    EDIT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: business[f.key] ?? "" }), {})
  );
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("Le nom est obligatoire");
      return;
    }
    setBusy(true);
    try {
      const r = await api.patch(`/admin/businesses/${business.id}`, form);
      toast.success("Fiche mise à jour");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`Éditer ${business.name}`} testid={`biz-edit-modal-${business.id}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        {EDIT_FIELDS.map((f) => (
          <label key={f.key} className="block">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">
              {f.label}
              {f.required && <span className="text-destructive"> *</span>}
            </div>
            <input
              type={f.type}
              value={form[f.key] || ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none"
              data-testid={`biz-edit-field-${f.key}`}
            />
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 text-sm bg-accent hover:bg-accent/70 rounded-sm" data-testid="biz-edit-cancel">
          Annuler
        </button>
        <button
          onClick={save}
          disabled={busy}
          className="px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white rounded-sm disabled:opacity-60"
          data-testid="biz-edit-save"
        >
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-card border border-border rounded-sm p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-lg font-medium mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}
